import React, { useMemo, useState, useEffect } from 'react';
import { Search, ClipboardCheck, X } from 'lucide-react';
import {
  CLINICAL_ASSESSMENT_SPEC,
  filterClinicalAssessmentCatalog,
  getClinicalAssessmentDomains,
  buildCatalogStatusMap,
  matchRecordedAssessmentNameToSlug,
  clinicalAssessmentCatalog,
} from '../../data/clinicalAssessmentCatalog';
import ClinicalAssessmentQuickFormModal from './ClinicalAssessmentQuickFormModal';

/**
 * Shared MedFast assessment library (35 core + custom per spec).
 * @param {{
 *   recordedAssessments?: Array<{ id?: string, name?: string, status?: string }>,
 *   residentName?: string,
 *   context?: 'resident' | 'clinical',
 *   onQuickFormSaved?: (payload: object) => void,
 *   residentId?: string|number,
 * }} props
 */
export default function AssessmentLibraryPanel({
  recordedAssessments = [],
  residentName = '',
  residentId,
  context = 'resident',
  onQuickFormSaved,
}) {
  const [query, setQuery] = useState('');
  const [domain, setDomain] = useState('all');
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [startSlug, setStartSlug] = useState(null);

  const domains = useMemo(() => getClinicalAssessmentDomains(), []);
  const filtered = useMemo(() => filterClinicalAssessmentCatalog(query, domain), [query, domain]);
  const statusBySlug = useMemo(() => buildCatalogStatusMap(recordedAssessments), [recordedAssessments]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const matched = recordedAssessments
      .map((r) => ({ name: r?.name, slug: matchRecordedAssessmentNameToSlug(r?.name) }))
      .filter((x) => x.slug);
    // eslint-disable-next-line no-console
    console.debug('[AssessmentLibraryPanel] catalog status map', {
      context,
      slugsWithData: Object.entries(statusBySlug).filter(([, v]) => v.status !== 'none').length,
      recordedMatches: matched,
    });
  }, [context, recordedAssessments, statusBySlug]);

  const startItem = startSlug ? clinicalAssessmentCatalog.find((a) => a.slug === startSlug) : null;

  const openQuickForm = (slug) => {
    setStartSlug(slug);
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[AssessmentLibraryPanel] start assessment', { slug, context, residentName: residentName || '(none)' });
    }
  };

  const badgeFor = (slug) => {
    const s = statusBySlug[slug]?.status;
    if (s === 'completed') {
      return (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-900 border border-emerald-200 shrink-0">
          Recorded
        </span>
      );
    }
    if (s === 'on-file') {
      return (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200 shrink-0">
          In progress
        </span>
      );
    }
    return null;
  };

  return (
    <>
      <div className="bg-white rounded-xl border-2 border-indigo-200 shadow-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-indigo-600 shrink-0" aria-hidden />
            Assessment library ({CLINICAL_ASSESSMENT_SPEC.totalCore} core)
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Regulatory timing, NQIP links, and care-plan mapping per{' '}
            <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{CLINICAL_ASSESSMENT_SPEC.markdownPath}</span>{' '}
            (v{CLINICAL_ASSESSMENT_SPEC.version}).
            {context === 'resident' ? (
              <>
                {' '}
                Rows show <strong>Recorded</strong> when a demo assessment matches this catalog entry.
              </>
            ) : (
              <>
                {' '}
                Open a resident profile to match recorded assessments; here you can browse tools and start quick forms (demo).
              </>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, domain, NQIP, or keyword…"
              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              aria-label="Filter assessment library"
            />
          </div>
          <label className="sr-only" htmlFor="assessment-library-domain-panel">
            Domain
          </label>
          <select
            id="assessment-library-domain-panel"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="sm:w-56 border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All domains</option>
            {domains.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-100">
          {filtered.map((item) => (
            <div
              key={item.slug}
              className="flex flex-col sm:flex-row sm:items-stretch gap-0 sm:gap-2 hover:bg-indigo-50/90 transition-colors"
            >
              <button
                type="button"
                onClick={() => {
                  setSelectedDetail(item);
                  if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.debug('[AssessmentLibraryPanel] open detail', { slug: item.slug, id: item.id, context });
                  }
                }}
                className="flex-1 text-left px-4 py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 min-w-0"
              >
                <div className="min-w-0">
                  <span className="font-semibold text-gray-900">
                    {item.id}. {item.name}
                  </span>
                  <span className="text-sm text-indigo-700 font-medium"> · {item.domain}</span>
                  <p className="text-xs text-gray-600 mt-1 leading-snug">{item.summary}</p>
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0 sm:justify-end items-start">
                  {badgeFor(item.slug)}
                  {item.nqip && (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200">
                      NQIP · {item.nqip}
                    </span>
                  )}
                  <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-800 border border-slate-200 max-w-[14rem] truncate"
                    title={item.due}
                  >
                    {item.due}
                  </span>
                </div>
              </button>
              <div className="flex items-center px-4 pb-3 sm:px-2 sm:py-3 sm:border-l border-gray-100">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openQuickForm(item.slug);
                  }}
                  className="text-xs font-semibold px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 whitespace-nowrap"
                >
                  Start
                </button>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-gray-500 py-10 text-sm">No assessments match your search or domain filter.</p>
        )}
      </div>

      {selectedDetail && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="assessment-catalog-modal-title"
          onClick={() => setSelectedDetail(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[min(90vh,640px)] overflow-y-auto p-6 border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start gap-3 mb-4">
              <h3 id="assessment-catalog-modal-title" className="text-xl font-bold text-gray-900 leading-tight pr-2">
                {selectedDetail.id}. {selectedDetail.name}
              </h3>
              <button
                type="button"
                aria-label="Close assessment details"
                onClick={() => setSelectedDetail(null)}
                className="p-2 rounded-lg hover:bg-gray-100 shrink-0 text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">Domain</dt>
                <dd className="text-gray-900 font-medium">{selectedDetail.domain}</dd>
              </div>
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">Quality standards</dt>
                <dd className="text-gray-800">{selectedDetail.standard}</dd>
              </div>
              {selectedDetail.nqip && (
                <div>
                  <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">NQIP</dt>
                  <dd className="text-amber-900 font-medium">{selectedDetail.nqip}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">When</dt>
                <dd className="text-gray-800">{selectedDetail.due}</dd>
              </div>
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">Who completes</dt>
                <dd className="text-gray-800">{selectedDetail.who}</dd>
              </div>
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">Purpose</dt>
                <dd className="text-gray-700 leading-relaxed">{selectedDetail.summary}</dd>
              </div>
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-gray-500">Care plan link</dt>
                <dd className="text-gray-700 leading-relaxed">{selectedDetail.carePlanLink}</dd>
              </div>
            </dl>
            {context === 'resident' && statusBySlug[selectedDetail.slug]?.status !== 'none' && (
              <p className="mt-3 text-xs font-medium text-indigo-800 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                Resident status:{' '}
                {statusBySlug[selectedDetail.slug].status === 'completed' ? 'Recorded (completed)' : 'On file (in progress)'}
                {statusBySlug[selectedDetail.slug].sourceName ? ` — “${statusBySlug[selectedDetail.slug].sourceName}”` : ''}
              </p>
            )}
            <p className="mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500">
              Scoring rules, field lists, auto-actions, and review matrix are defined in{' '}
              <span className="font-mono">Compliance.md §6-7</span>. Assessment persistence and parity with{' '}
              <span className="font-mono">MD/Process/Resident.md</span> are tracked in the project todo list.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => setSelectedDetail(null)}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-800 font-semibold text-sm hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  const slug = selectedDetail.slug;
                  setSelectedDetail(null);
                  openQuickForm(slug);
                }}
                className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700"
              >
                Start assessment
              </button>
            </div>
          </div>
        </div>
      )}

      <ClinicalAssessmentQuickFormModal
        slug={startSlug}
        catalogItem={startItem}
        residentName={residentName}
        residentId={residentId}
        onClose={() => setStartSlug(null)}
        onComplete={(payload) => {
          onQuickFormSaved?.(payload);
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.debug('[AssessmentLibraryPanel] quick form completed', payload);
          }
        }}
      />
    </>
  );
}
